import { RangePattern, TypePattern } from '@providers'
import { IState } from 'components/AdminFloorPlan/hooks/useSpaceDetails'

// Enums
export enum Role {
  ADMIN = 'admin',
  DEPARTMENT_ADMIN = 'dept_admin',
  LOCATION_ADMIN = 'location_admin',
  IT = 'it',
  VIEWER = 'viewer',
  POWER_ADMIN = 'power_admin',
  SPACE_CAPTAIN = 'space_captain',
  BOOKINGS_MANAGER = 'bookings_manager',
}

export enum Flag {
  FLOOR_PLAN = 'apolloFloorPlan',
  SISENSE = 'sisense',
}

export enum TicketStatus {
  NEW = 'new',
  OPEN = 'open',
  INPROGRESS = 'inprogress',
  PENDING = 'pending',
  ONHOLD = 'onhold',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
  CANCELED = 'canceled',
  ASSIGNED = 'assigned',
  ERROR = 'error',
}

export enum TicketPriority {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
  INFO = 'info',
}

export enum Order {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum NumberType {
  NUMBER,
  ROUNDED_NUMBER,
  DOLLAR,
  ROUNDED_DOLLAR,
}

// Interfaces
export interface IEdge<T> {
  node: T
}

export interface IRecord {
  id: string
}

export interface IURL {
  url: string
}

export interface IUser {
  id: string
}

export interface IPageInfo {
  endCursor: string
  hasNextPage: boolean
}

export interface IPaginationInfo {
  currentPage: number
  perPage: number
  total: number
  totalPages: number
}

export interface IPaginationParams {
  perPage?: number
  pageNumber?: number
}

export interface IConnectionWithTotal<T> {
  counters: { total: number }
  pageInfo: IPageInfo
  edges: IEdge<T>[]
}

export interface IConnection<T> {
  pageInfo: IPageInfo
  edges: IEdge<T>[]
}

// ImportAttributeFragment
export interface IImportAttribute {
  changed: boolean
  oldValue: string
  value: string
}

// ImportEmployeeFragment
export interface IImportEmployee {
  externalId: string
  name: string
  firstName: string
  lastName: string
  email: string
  phone: string
  dept: { name: string }
  reportingTo: string
  team: { name: string }
  location: string
  title: string
  employmentType: string
  badge: string
}

// CompanyAssetFragment
export interface ICompanyAsset {
  fileName: string | null
  id: string
  updatedAt: string | null
  url: string | null
}

export interface IUploadedImageAsset extends ICompanyAsset {
  fileName: string
  updatedAt: string
  url: string
  isDefault: boolean
}

export type InputAsset = {
  content: string
  fileName: string
  isDefault: boolean
}

export interface IDateRange {
  fromDate: Date
  toDate: Date
}

// Local file in the browser
export interface IFile extends Blob {
  content: string
  name: string
  path: string
  type: string
  lastModifiedDate: Date
}

export interface IUploadFile {
  content: string
  name: string
}

// Api provided file
export interface IAsset {
  id: string
  fileName: string
  name?: string
  url: string
  createdAt: Date
  updatedAt: Date
  fullSizeUrl: string
}

export interface IContractAsset extends IAsset {
  contentType: string
  fileSize: number
}

export interface INormalizedObjects<T> {
  ids: string[]
  byId: { [id: string]: T }
}

export interface IDHash<T> {
  [id: string]: T
}
export interface IReportSection {
  name: string
  span: number
}

export type FormErrors<T> = {
  [key in keyof T]?: any
}

// lint disable for type not PascalCased
// eslint-disable-next-line
export interface graphQLError {
  message: string
  kind: string
  invalidArguments: { [key: string]: string }
  unknownErrors?: UnknownError[]
}

export interface UnknownError {
  message: string
  kind: string
  attribute: string
  modelType: string
}

export type graphQLErrors = graphQLError[]

//ScenarioGroupFragment
export interface IScenarioGroup {
  id: string
  deployAt: Date
  assignmentPlans: IAssignmentPlan[]
}

//AssignmentPlanFragment
export interface IAssignmentPlan {
  id: string
  name: string
  alias: string | null
  apType: 'master' | 'scenario'
  deployAt: Date
  createdAt: Date
  parentAssignmentPlanId: string | null
  createdBy: { name: string }
}

//SpaceTagFragment
export interface ITag {
  id: string
  name: string
}

//CostCenterFragment
export interface ICostCenter {
  id: string
  name: string
}

export interface IEmployee {
  id: string
  name: string
  email: string
  assets?: IAsset[]
  department?: { id: string }
  dept?: { name: string; id: string }
  team?: { name: string; id: string } | null
  spaceAssignment?: IEmployeeSpaceAssignment | null
}

export interface IFullEmployee extends IEmployee {
  phone: string
  note: string
  dept: IDepartment
  team: IDepartment | null
  startDate: string
  endDate: string
  employmentType: string
  location: string
  externalId: string
  costCenter?: {
    id: string
    name: string
  }
  manager?: {
    id: string
    name: string
  }
  badge: string
  geoLocation?: {
    location: string
  }
  role: Role
  tags: ITag[]
  workplaceGroups: IWorkplaceGroup[]
  measurements: number
  isRemote?: boolean
  noDeskNeeded?: boolean
  neighborhoods?: INeighborhood[] | null
  title?: string | null
}

export interface IMultiSelect {
  id: string
  name: string
}

export interface IFloor {
  id: string
  name: string
  building?: { id: string }
  notifications?: IContractNotification[]
}

export interface IHRLocation {
  id: string
  location: string
}

export interface IBuilding {
  id: string
  name: string
  city: string
  state: string
  portfolio?: IPortfolioBuilding
}

export interface ISpaceType {
  id: string
  name: string
  displayName: string
  interactivityType: number
  hidden: boolean
  interactivity?: string
}

export interface ISpaceTypes {
  id: string
  name: string
  displayName: string
  interactivityType: number
  hidden: boolean
  interactivity: string
}

export interface ISpaceTypeWithCompany extends ISpaceType {
  company: {
    id: string
  } | null
}

export interface IUsageType {
  id: string
  name: string
  displayName: string
}

export interface ICalendar {
  id: string
  calendarName?: string
  start: string
  end: string
}

export interface ISpace {
  tags: ITag[]
  id: string
  code: string
  externalId: string
  name: string
  spaceType: ISpaceType
  usageType: IUsageType | null
  isBookableUsageType: boolean
  isBookableByEmployee: boolean
  isAvailable: boolean
  spaceAssignment: ISpaceAssignment
  workplaceGroup: IWorkplaceGroup | null
  bookings?: IBooking[]
  globalMapObject?: IGlobalMapObject
  measurements: number
  capacity?: number
  comment?: string
  calendarId: string
  floor: {
    building: {
      timezone: string
    }
    id: string
  }
}

export interface IFloorMapSpace extends ISpace {
  w: number
  x: number
  y: number
  comment: string
  density: number
  status: string
  calendar?: ICalendar | null
}

//SpaceAssignmentFragment
export interface IEmployeeSpaceAssignment {
  space: {
    id: string
    externalId: string
    floor: {
      id: string
    }
  }
}

//AssetTagFragment
export interface IAssetTag {
  id: string
  name: string
  totals: {
    employees: number
    spaces: number
  }
}

interface IDayMax {
  days: number
}

interface IWeekMax {
  weeks: number
}

interface IMonthMax {
  months: number
}

export type MaxDuration = IDayMax | IWeekMax | IMonthMax

export interface LocationWorkingHoursGroupI {
  key: string
  timeFrom: string
  timeTo: string
  isBeingEdited: boolean
  isNewItem: boolean
  isChanged: boolean
  isLoading: boolean
  floorIds: string[]
}

//SettingsFragment
export interface ISettings {
  employeeImportNotificationEnabled: boolean
  hotelingDeskDeptEnforcementEnabled: boolean
  overrideBookingRestrictionEnabled: boolean
  enforceAuthAppEnabled: boolean
  unallocateSpaceOnUnseat: boolean
  hotelingBookingOverlapRestrictionEnabled: boolean
  hotelingDeskWpgEnforcementEnabled: boolean
  hotelingDeskWpgEnforcementOperator: 'AND' | 'OR'
  enablePolling: boolean
  spaceAvailabilityEnabled: boolean
  bookingAutoReleaseEnabled: boolean
  bookingCheckInRequired: boolean
  hourlyBookingEnabled: boolean
  dailyBookingEnabled: boolean
  ssoEnabled: boolean
  qrCodeEnabled: boolean
  cc: string[]
  bookingWorkdayTimeFrom: string
  bookingWorkdayTimeTo: string
  enableHealthScreening: boolean
  healthScreeningMessage: string
  healthScreeningConfirmButtonText: string
  hoursAdminsGetHealthNoticeAfterEmployees: number
  hoursHealthMessageSentBeforeBooking: number
  enableBookingCapacityCap: boolean
  companyBookingCapacityCap: number
  enableLocationBookingCapacityCap: boolean
  viewPeerCurrentBookings: boolean
  showOtherBookers: boolean
  enableRecurringBookings: boolean
  maxFutureBookingDuration: MaxDuration
  autoReleaseGracePeriod: number
  healthNoticeEnabled: boolean
  bookingCheckInCancelEnabled: boolean
  locationHealthCheckDisabled: ILocation[]
  hoursBeforeBookingCheckin: number
  employeeImportApprovalEnabled: boolean
  employeeImportApprovalThresholdPercent: number
  bookableSpaceTypes: string[]
  bookableSpaceTypesLocationsOverridesEnabled: boolean
  bookingReminderEnabled: boolean
  bookingConfirmationEnabled: boolean
  locationWorkingHoursGroups: LocationWorkingHoursGroupI[]
  bookingCancellationEnabled: boolean
  bookingNotesEnabled: boolean
  overrideSpaceTypeBookingRestrictionEnabled: boolean
  overriddenBookingRestrictionSpaceTypes: ISpaceType[]
  bookingConfirmationOnbehalfEnabled: boolean
  bookingCancellationOnbehalfEnabled: boolean
  bookingCheckinReminderOnbehalfEnabled: boolean
}

//AssetTagSpaceDetailsFragment
export interface IAssetDetailsSpace extends ISpace {
  spaceAssignment: {
    department: {
      id: string
      name: string
      color: string
      totals: {
        teams: number
      }
      team: ITeam
      parentDepartment: IDepartment | null
    }
    team: IDepartment
    employee: IFullEmployee | null
    sharedEmployees: IFullEmployee[] | null
    neighborhood: INeighborhood | null
  }
}

//NeighborhoodFragment
export interface INeighborhood {
  id: string
  name: string
  color: string
  floor: {
    id: string
    name: string
    building: {
      name: string
      city: string
    }
  }
  employees: IFullEmployee[]
}
export interface IWorkplaceGroup {
  id: string
  name: string
  color: string
}

export type INeighborhoodSearch = INeighborhood

export interface INeighborhoodSpace {
  id: string
  code: string
  name: string
  externalId: string
  spaceAssignment: {
    department: {
      name: string
    }
    team: {
      name: string
    }
  }
  usageType: IUsageType
  spaceType: ISpaceType
}

export interface IWorkplaceGroupSpace {
  id: string
  externalId: string
  name: string
  usageType: IUsageType
  spaceType: ISpaceType
  spaceAssignment: {
    department: { name: string }
    team: { name: string }
  }
}

export interface IProjection {
  id: string
  name: string
  color: string
}

export interface IProjectionConnectionWithTotal {
  edges: IEdge<IProjectionImport>[]
}

export interface IProjectionFailedImportError {
  errors: {
    building: string
    floor?: string
    department: string
  }
  object: {
    buildingCode: string
    departmentName: string
    date: string
    rowNum: number
    numberAmount: number
  }
}
export interface IProjectionImport {
  id: string
  importedBy: string
  status: string
  totalCount: number
  createdAt: string
  createdCount: number
  updatedCount: number
  failedCount: number
  skippedCount: number
  url: string
  failed: IProjectionFailedImportError[]
}
export interface IProjectionImportDetail {
  id: string
  importedBy: string
  status: string
  error: string
  rowNum: number
  date: string
  departmentName: string
  buildingCode: string
  floorCode?: string
}

export interface INeighborhoodTableRecord extends INeighborhood {
  counters: {
    employees: number
    workstations: number
    occupancy: number
    maxEmployees: string
    sharingRatio: number
  }
  createdBy: {
    employee: IEmployee
  } | null
  createdAt: Date
}
export interface IWorkplaceGroupTableRecord extends IWorkplaceGroup {
  totals: {
    spaces: number
    employees: number
  }
  createdBy: {
    employee: IEmployee
  }
  createdAt: Date
}
export interface IWorkplaceGroupEmployee extends IEmployee {
  workplaceGroup: IWorkplaceGroup
}

export interface IWorkplaceGroupDetails extends IWorkplaceGroup {
  spaces: {
    counters: {
      total: number
    }
  }
  employees: {
    counters: {
      total: number
    }
  }
  createdBy: {
    employee: IEmployee
  }
  createdAt: Date
}

export interface INeighborhoodEmployeesConnectionWithTotal {
  employeeConnection: {
    edges: IEdge<IEmployee>[]
    counters: {
      total: number
    }
    pageInfo: IPageInfo
  }
}

export interface INeighborhoodImport {
  createdAt: string
  createdCount: number
  failed: INeighborhoodImportFailed[]
  failedCount: number
  id: string
  importedBy: string
  source: string
  status: string
  totalCount: number
  updatedCount: number
  url: string
}

export interface INeighborhoodImportFailedError {
  base: string | null
  createdAt: string | null
  employeeId: string | null
  id: string | null
  updatedAt: string | null
}

export interface INeighborhoodImportVersion {
  event: string
  id: string
  versionId: string
  employee_neighborhood: IEmployeeNeighborhood | null
}

export interface INeighborhoodImportFailed {
  employeeNeighborhood: IEmployeeNeighborhood | null
  errors: INeighborhoodImportFailedError | null
}

export interface IEmployeeNeighborhood {
  employee: {
    name: string
    email: string
    department: {
      name: string
    }
  }
  id: string
  neighborhood: {
    name: string
    floor: {
      name: string
      building: {
        name: string
        city: string
      }
    }
  }
}

export interface INeighborhoodSpacesConnectionWithTotal {
  spaceConnection: {
    edges: IEdge<INeighborhoodSpace>[]
    counters: {
      total: number
    }
    pageInfo: IPageInfo
  }
}

export interface IWorkplaceGroupEmployeesConnectionWithTotal {
  employees: {
    edges: IEdge<IWorkplaceGroupEmployee>[]
    counters: {
      total: number
    }
    pageInfo: IPageInfo
  }
}

export interface INeighborhoodDetails extends INeighborhoodTableRecord {
  owner: {
    id: string
    employee: IEmployee
  }
}

export interface IDepartmentBase {
  id: string
  name: string
  color: string
}

//DepartmentFragment
export interface IDepartment extends IDepartmentBase {
  totals: {
    teams: number
  }
  team: ITeam
  parentDepartment: IDepartment | null
}

export interface IDepartmentTeams extends IDepartmentBase {
  id: string
  name: string
  color: string
  teams: IDepartmentBase[]
}

export interface IBookingManagerSetting {
  manage_all: boolean
  managed_departments?: { employeeConnection: { edges: { node: IEmployee }[] } }[]
  managed_employees?: IEmployee[]
  allManagableEmployees?: IEmployee[]
}

//DMTableDepartmentFragment
export interface IDMTableDepartment extends IDepartment {
  colorUpdatedAt: string | null
  updatedBy: {
    employee: IEmployee
  } | null
  totals: {
    employees: number
    spaces: number
    teams: number
  }
}

export interface IDMDialogSpace extends ISpace {
  spaceAssignment: {
    department: {
      id: string
      name: string
      color: string
      totals: {
        teams: number
      }
      team: ITeam
      parentDepartment: IDepartment | null
    }
    team: {
      id: string
      name: string
      color: string
      totals: {
        teams: number
      }
      team: ITeam
      parentDepartment: IDepartment | null
    }
    employee: IFullEmployee | null
    sharedEmployees: IFullEmployee[] | null
    neighborhood: INeighborhood | null
  }
}

//EmployeeTableRecordFragment
export interface IEmployeeTableRecord {
  id: string
  name: string
  title: string
  email: string
  employmentType: string
  noDeskNeeded: boolean
  isRemote: boolean
  assets: IAsset[]
  imported: boolean
  dept: IDepartment
  team: IDepartment | null
  manager: IEmployee
  geoLocation: IHRLocation
  spaceAssignment: IEmployeeSpaceAssignment | null
  secondarySpaceAssignments: IEmployeeSpaceAssignment[]
  neighborhoods: INeighborhood[]
  department: IDepartment
}

//EmployeeDetailsFragment
export interface IEmployeeDetails extends IEmployeeTableRecord {
  role: Role
  user: {
    bookingManagerSetting: IBookingManagerSetting
  }
  phone: string | null
  badge: string
  note: string
  startDate: Date | null
  endDate: Date | null
  externalId: string
  costCenter: { name: string }
  extAttributes: string
  slackHandle: string
  departmentAdmins?: {
    department: {
      id: string
    }
  }[]
  locationAdmins: { floorPlan: IFloor & { building: IBuilding } }[]
}

//EmployeeConnectionWithTotalFragment
export interface IEmployeeConnectionWithTotal {
  edges: IEdge<IEmployeeTableRecord>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

export interface ISpacesConnectionWithTotal {
  edges: IEdge<ISpace>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

export interface IWorkplaceGroupConnectionWithTotal {
  edges: IEdge<IWorkplaceGroupTableRecord>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

export interface IBuildingsConnectionWithTotal {
  edges: IEdge<BMTableBuilding>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

export interface INeighborhoodConnectionWithTotal {
  edges: IEdge<INeighborhoodTableRecord>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

//EmployeeImportHistoryFragment
export interface IEmployeeImport {
  id: string
  createdAt: Date
  status: string
  source: string
  totalCount: number
  createdCount: number
  destroyedCount: number
  seatedCount: number
  updatedCount: number
  failedCount: number
  importedBy: string
  url: string
  backupUrl: string
  updatedAt: string
}

export interface IDashboardEmployeeImport {
  id: string
  createdAt: Date
  status: string
  source: string
  totalCount: number
  createdCount: number
  destroyedCount: number
  seatedCount: number
  updatedCount: number
  failedCount: number
  importedBy: string
  updatedAt: string
  autoCancelAt: string
}

export interface IBookingImport {
  id: string
  createdAt: Date
  status: string
  source: string
  totalCount: number
  createdCount: number
  failedCount: number
  importedBy: string
  url: string | null
}

//ImportProgressFragment
export interface IImportProgress {
  progress: number
  total: number
}

//EmployeeImportVersionFragment
export interface IEmployeeImportVersion {
  id: string
  event: string
  externalId: IImportAttribute
  name: IImportAttribute
  firstName: IImportAttribute
  lastName: IImportAttribute
  email: IImportAttribute
  phone: IImportAttribute
  departmentName: IImportAttribute
  reportingTo: IImportAttribute
  teamName: IImportAttribute
  location: IImportAttribute
  title: IImportAttribute
  employmentType: IImportAttribute
  badge: IImportAttribute
  employee: IImportEmployee
}

//EmployeeImportFailedFragment
export interface IEmployeeImportFailed {
  employee: IImportEmployee
  errors: {
    externalId: string
    name: string
    firstName: string
    lastName: string
    email: string
    phone: string
    reportingTo: string
    location: string
    title: string
    employmentType: string
    departmentId: string
    badge: string
  }
}

export interface IBookingImportVersion {
  id: string
  event: string
  booking: IBooking | null
}

export interface IBookingImportFailedError {
  autoReleasedAt: string | null
  checkedIn: string | null
  checkinEmailSentAt: string | null
  createdById: string | null
  deletedAt: string | null
  employeeId: string | null
  endDate: string | null
  id: string | null
  spaceId: string | null
  startDate: string | null
  base: string | null
}

export interface IBookingImportFailed {
  booking: IBooking | null
  errors: IBookingImportFailedError | null
}

//CatalogEmployeeFragment
export interface ICatalogEmployee extends IFullEmployee {
  noDeskNeeded: boolean
  isRemote: boolean
  secondarySpaceAssignments: IEmployeeSpaceAssignment[]
  neighborhoods: INeighborhood[]
}

export interface ICatalogEmployeeConnectionWithTotal {
  edges: IEdge<ICatalogEmployee>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

//ManagedDepartmentFragment
export interface IManagedDepartment {
  department: IDepartment
}
export interface IManagedEmployee {
  employee: IEmployee
}

export interface IFloorWithBuilding extends IFloor {
  building: {
    id: string
    name: string
    address: string
    region: string
  }
}

//FloorPlanFragment
export interface IFloorPlan {
  floorPlan: IFloorWithBuilding
}

export interface MeridianMap {
  externalId: string
}

export interface CreateBuildingInput {
  name: string
  address: string
  code: string
  geoLocationId: string
  closedAt: string | null
  buildingTypeId: string | null
  meridianLocation: { externalId: string } | null
}

export interface UpdateBuildingInput extends CreateBuildingInput {
  id: string
}

export interface CreateFloorInput {
  story: number
  capacity: number
  code: string
  name: string
  scale: number
  building_id: string
  meridianMap: { externalId: string } | null
  rentableArea: number
  safeDistance: number
  usableArea: number
  areaSize: string
  printableQrCodeUsageTypes: string[]
}

export type UpdateFloorInput = Partial<Omit<CreateFloorInput, 'building_id'>> & {
  id: string
  hidden?: boolean
  bookingWorkdayTimeFrom?: string | null
  bookingWorkdayTimeTo?: string | null
}

export type RemoveFloorSpecificBusinessHourInput = {
  floorIds: string[]
}

export type AddFloorSpecificBusinessHourInput = RemoveFloorSpecificBusinessHourInput & {
  bookingWorkdayTimeFrom: string | null
  bookingWorkdayTimeTo: string | null
}

export interface CreateFloorAssetsVersionInput {
  floorId: string
  mapW: number
  mapH: number
  background?: IUploadFile
  autocad?: IUploadFile
}

export type UpdateFloorAssetsVersionInput = Omit<CreateFloorAssetsVersionInput, 'floorId'> & {
  versionId?: string
}

export interface IAssetVersion {
  id: string
  background: IAsset | null
  autocad: IAsset | null
  mapW: number
  mapH: number
}

export interface IBuildingType {
  id: string
  name: string
  displayName: string
}

export interface IBuildingTypeWithCompany extends IBuildingType {
  company: {
    id: string
  } | null
}

export interface BMTableFloor {
  id: string
  name: string
  hidden: boolean
  story: number
  code: string
  capacity: number
  areaSize: number
  usableArea: number
  rentableArea: number
  safeDistance: number
  assetVersion: {
    background: IAsset | null
    updatedAt: string
  }
}

export interface BMDialogFloor {
  id: string
  name: string
  hidden: boolean
  story: number
  code: string
  capacity: number
  areaSize: number
  safeDistance: number
  usableArea: number
  rentableArea: number
  assetVersion: IAssetVersion | null
  building: { id: string; code: string }
  enforceQrCode: boolean
  printableQrCodeUsageTypes: string[]
  scale: number
  meridianMap: MeridianMap | null
}

export interface BMTableBuilding {
  id: string
  name: string
  city: string
  state: string
  address: string
  code: string
  geoLocation: IHRLocation
  floors: BMTableFloor[]
  closedAt: Date | null
  buildingType: IBuildingType
}

export interface BMDialogBuilding {
  id: string
  name: string
  address: string
  code: string
  closedAt: Date | null
  geoLocation: IHRLocation
  buildingType: IBuildingType
  meridianLocation: {
    externalId: string
  } | null
  floors: {
    id: string
    name: string
  }[]
}

export interface IBuildingDetails {
  id: string
  code: string
  name: string
  address: string
  city: string
  state: string
  closedAt?: string | null
  floors: IFloorDetails[] | null
}

export interface BMBuilding {
  id: string
  name: string
  address: string
  code: string
  closedAt: Date
  geoLocation: {
    id: string
    location: string
  }
  meridianLocation: {
    id: string
    externalId: string
  }
}

export interface IFloorDetails {
  id: string
  name: string
  code: string
  hidden: boolean
  story: number
  bookingWorkdayTimeFrom: string | null
  bookingWorkdayTimeTo: string | null

  building: IBuilding & {
    address: string
    city: string
    state: string
    closedAt?: string | null
    code: string
    region: string
  }
}

export interface ITicket {
  id: string
  ticketNumber: string
  source: string
  externalUrl: string | null
  createdDate: string
  dueDate: string | null
  shortDescription: string
  severity: TicketPriority
  status: TicketStatus
  employee: IEmployee
  assignedTo: IEmployee | null
}

export interface ITicketDetails extends ITicket {
  description: string
  building: IBuilding | null
  floor_plan: IFloor | null
}

export interface TicketSearchResult {
  id: string
  ticketNumber: string
  shortDescription: string
  status: TicketStatus
  assignedTo: IEmployee | null
}

interface ILeaseBase {
  [index: string]: any
  contractName?: string
  contactInfo?: string
  cost: string
  endDate?: Date
  noticeDate?: Date
  startDate?: Date
  endAlert?: boolean
  files: IAsset[]
  building?: ILeaseBuilding
  floor?: ILeaseFloor
  name?: string
  notes?: string
  noticeAlert?: boolean
  owner?: string
  title?: string
  employees: IEmployee[]
}
export interface INewLease extends ILeaseBase {
  metadata: ILeaseMetadata
}
export interface ILease extends ILeaseBase {
  id: string
  createdAt: Date
  metadata: ILeaseMetadata
  monthlyCost: string
  totalCost: string
  costPerSqft: string
  contractEvents: ILeaseFullEvent[]
}

export interface ILeaseMetadata {
  leaseArea?: string
  otherCost?: string
  suite?: string
  usableLeaseArea?: string
  otherCurrencyType?: string
  propertyType?: string
  leasePremises?: string
  renewalOptions?: string
}

export interface ILeaseCounters {
  leases: number
  buildings: number
  floors: number
}
export interface ILeaseFloor {
  id: string
  name: string
  building: ILeaseBuilding
}

export interface ILeaseBuilding {
  id: string
  address: string
  name: string
  city: string
}

export interface ILeaseEvent {
  id: string
  amount: number
  date: Date | null
  description: string
  eventType: string | ILeaseEventType
  units: string | ILeaseEventUnit
  dateAlert: boolean
}

export interface ILeaseFullEvent extends ILeaseEvent {
  createdAt: string
  createdBy: {
    name: string
  } | null
  files: IAsset[]
}

export interface ILeaseConnectionWithTotal {
  edges: {
    node: ILease[]
  }
  counters: ILeaseCounters
  exportList: string
  pageInfo: {
    endCursor: string | null
    hasNextPage: boolean
  }
}
export interface ILeaseEventWithFiles extends ILeaseEvent {
  files: IAsset[]
}

export type ILeaseEventType = 'Renewal' | 'Sublease' | 'Termination' | 'Other'
export type ILeaseEventUnit = '$' | 'Sqft' | '$/sqft' | 'Other'
export type LeaseDateFilterKey = 'endDate' | 'startDate' | 'noticeDate'

interface ILeaseListDateFilters {
  key: LeaseDateFilterKey
  value: {
    toDate: string | undefined
    fromDate: string | undefined
  }
}

export interface ILeaseListVariables {
  first: number
  dateFilters: ILeaseListDateFilters[]
  after: string | null
  sortBy?: string
  order?: Order
  cities?: string[]
  query?: string
  type?: string
}

export enum LeaseFilterKeys {
  CONTRACT = 'ContractId',
  FLOOR = 'FloorId',
}
export interface ILeaseKeyValueFilter {
  key: LeaseFilterKeys
  value: string | null
}

export interface ILeaseSearchResult {
  id: string
  title: string
}

export enum LeaseDateStates {
  EXPIRED = 'var(--grey1)',
  DANGER = 'var(--red)',
  WARNING = 'var(--orange)',
}

// ***********************
export interface IFloorMapBlock {
  id: string
  url: string
}

export interface IFloorMapBlockInstance {
  id: string
  block: IFloorMapBlock
  map: {
    x: number
    y: number
    w: number
    h: number
    rotation: number
  }
}

export interface IFloorAssetVersion {
  mapW: number
  mapH: number
  background: { url: string }
  blocks: IFloorMapBlock[]
  blockInstances: IFloorMapBlockInstance[]
}

export interface IFloorMapEmployee {
  id: string
  name: string
  phone: string
  email?: string
  location?: string
  startDate?: string
  endDate?: string
  badge?: string
  externalId?: string
  note: string
  employmentType?: string
  costCenter?: {
    id: string
    name: string
  }
  manager?: {
    id: string
    name: string
  }
  assets?: IAsset[]
  dept: IDepartment
  team: IDepartment | null
}

export interface IFloorMap {
  id: string
  scale: number
  safeDistance: number
  capacity: number
  hidden: boolean
  assetVersion: IFloorAssetVersion
  building: {
    geoLocation: {
      id: string
    }
    timezone: string
  }
}

export interface ITeam {
  id: string
  name?: string
}

export interface ISpaceAssignment {
  employee: IFullEmployee | null
  sharedEmployees: IFullEmployee[] | null
  department: IDepartment | null
  team: IDepartment | null
  neighborhood: INeighborhood | null
  sharedDepartments?: IFullEmployee[] | null
}

export interface IGlobalMapObject {
  id: string
  icon: string
  color: string
  name: string
  fontFamily: string
  limitOnePerFloor: boolean
}

export interface IFloorMapPin {
  id: string
  map: {
    x: number
    y: number
    w: number
    h: number
    name: string
  }
  globalMapObject: IGlobalMapObject
}

export interface ISpaceDetails extends IFloorMapSpace {
  capacity: number
  bookings?: IBooking[]
  measurements: number
  department: IDepartmentBase | null
  workplaceGroup: IWorkplaceGroup | null
  tags: ITag[]
  tagNames: string[]
  employee: IEmployee | undefined | null
  assets: IUploadedImageAsset[]
  calendar: ICalendar | null
  calendarId: string
  data: IState
}

export interface ISpaceCoordinates {
  x: number
  y: number
}

export interface IMoveOrderSpace {
  id: string
  code: string
  externalId: string
  spaceType: ISpaceType
  usageType: IUsageType
  spaceAssignment: ISpaceAssignment
  calendar?: ICalendar | null
}

export interface MoveOrder {
  id: string
  fromSpace: IMoveOrderSpace
  toSpace: IMoveOrderSpace
}

export interface IPortfolioRegion {
  name: string
  id: string
  cities: IPortfolioCity[]
}

export interface IPortfolioCity {
  id: string
  name: string
  buildings: IBuildingDetails[]
}
export interface IPortfolioBuilding {
  areaSqft: number
  areaSqrm: number
  densitySqft: number
  densitySqrm: number
  occupancy: number
  unallocated: number
  unoccupied: number
  occupied: number
  workstations: number
  seatedCount: number
  allocated: number
  spaceTypes: IPortfolioSpace[]
  spaceUsageTypes: IPortfolioSpaceUsageTypes[]
  building: IPortfolioBuildingDetails
  floors: IPortfolioFloor[]
  departments: IPortfolioDepartment[]
  notifications: IContractNotification[]
}

export type ContractNotificationEventType =
  | 'LEASE_NOTICE_DATE'
  | 'LEASE_REMINDER_DATE'
  | 'LEASE_EFFECTIVE_DATE'
  | 'LEASE_END_DATE'

export interface IContractNotification {
  id: string
  createdAt: string
  name?: string
  description?: string
  eventType: ContractNotificationEventType
  data: {
    id: string
    noticeDate?: string
    reminderDate?: string
    effectiveDate?: string
    endDate?: string
  }
}

export interface IBookingSpace {
  id: string
  code: string
  name: string
  spaceType: ISpaceType
  externalId: string
  usageType: IUsageType
  isBookableUsageType: boolean
  spaceAssignment: ISpaceAssignment
  floor: {
    id: string
    name: string
    building: {
      id: string
      name: string
      address: string
      city: string | null
      timezone: string
    }
  }
}

export interface IBookingEmployee extends IFullEmployee {
  title: string | null
}

export enum RecurrencePatternType {
  ABSOLUTE_MONTHLY = 'absolute_monthly',
  DAILY = 'daily',
  MULTI_DAY = 'multi_day',
  WEEKLY = 'weekly',
  SINGLE_DAY = 'single_day',
}

export const RecurrencePatternTypeMap = {
  [RecurrencePatternType.ABSOLUTE_MONTHLY]: 'Recurring',
  [RecurrencePatternType.DAILY]: 'Recurring',
  [RecurrencePatternType.MULTI_DAY]: 'Multi-Day',
  [RecurrencePatternType.WEEKLY]: 'Recurring',
  [RecurrencePatternType.SINGLE_DAY]: 'Single-Day',
}
export const getRecurrencePatternType = (
  recurrencePattern: RecurrencePatternType = RecurrencePatternType.SINGLE_DAY
) => RecurrencePatternTypeMap[recurrencePattern]

export interface IBookingSchedule {
  id: string
  recurrencePattern: RecurrencePatternType
  effectiveEndDate: string
  effectiveStartDate: string
  pattern: {
    dayOfMonth: number
    daysOfWeek: number
    interval: number
    type: TypePattern
  }
  range: {
    endDate: string
    numberOfOccurrences: number
    startDate: string
    type: RangePattern
  }
}

export interface IBooking {
  autoReleasedAt: any
  id: string
  startDate: string
  endDate: string
  effectiveStartDate?: string
  effectiveEndDate?: string
  bookingSchedule?: IBookingSchedule
  employee: IBookingEmployee
  bookedBy: {
    employee: IBookingEmployee
  } | null
  note: string
  space: IBookingSpace
  passedHealthCheck: boolean
  checkedIn: boolean
  disabled?: boolean
  healthCheck: {
    disabled: boolean
    locationHeathCheckGroup: {
      id: string
    }
  }
  summary?: string
  calendar?: ICalendar | null
}

export interface ICalendarEvents {
  id: string
  start: string
  end: string
  summary: string
  calendar: {
    space: ISpace
  }
}

export interface IBookingsConnectionWithTotal {
  edges: IEdge<IBooking>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

export interface IPortfolioTooltipBuilding {
  areaSqft: number
  areaSqrm: number
  occupancy: number
  unallocated: number
  unoccupied: number
  occupied: number
  seatedCount: number
  workstations: number
  spaceUsageTypes: IPortfolioTooltipSpaceUsageType[]
  floors: IPortfolioTooltipFloor[]
  departments: IPortfolioTooltipDepartment[]
}
export interface IPortfolioBuildingDetails {
  id: string
  name: string
  code: string
  address: string
  leaseAnnualCost: number
  closedAt: string
  meridianLocation: {
    externalId: string
  }
  leases: {
    id: string
    title: string
    cost: string
    endDate: string
  }
  city: string
  lat: number
  long: number
  contact: {
    name: string
    phone: string
  }
  geoLocation: {
    id: string
    location: string
  }
  notifications: IContractNotification[]
  floors: IPortfolioFloor[]
}
export interface IPortfolioSpace {
  areaSqft: number
  areaSqrm: number
  occupied: number
  readableType: string
  spaceType: string
  workstations: number
}
export interface IPortfolioSpaceUsageTypes {
  allocated: number
  areaSqft: number
  areaSqrm: number
  floorArea: number
  occupancy: number
  occupied: number
  readableType: string
  spaceUsageType: string
  unoccupancy: number
  unoccupied: number
  workstations: number
}

export interface IPortfolioTooltipSpaceUsageType {
  areaSqft: number
  areaSqrm: number
  readableType: string
  spaceUsageType: string
  workstations: number
}

export interface IPortfolioTooltipDepartment {
  unoccupied: number
  allocated: number
  occupied: number
  areaSqft: number
  areaSqrm: number
  seatedCount: number
  department: {
    name: string
    color: string
  }
}
export interface IPortfolioTooltipSpaceUsageType {
  workstations: number
  areaSqft: number
  areaSqrm: number
  readableType: string
  spaceUsageType: string
}

export interface IPortfolioFloor {
  allocated: number
  unallocated: number
  workstations: number
  unoccupied: number
  occupied: number
  seatedCount: number
  areaSqft: number
  areaSqrm: number
  densitySqft: number
  densitySqrm: number
  occupancy: number
  departments: IPortfolioDepartment[]
  spaceUsageTypes: IPortfolioSpaceUsageTypes[]
  spaceTypes: IPortfolioSpace[]
  floor: {
    id: string
    name: string
    hidden: boolean
    story: number
    building: {
      id: string
    }
    notifications: IContractNotification[]
  }
}

export interface IPortfolioTooltipFloor {
  spaceUsageTypes: IPortfolioTooltipSpaceUsageType[]
  departments: IPortfolioTooltipDepartment[]
  floor: {
    id: string
    name: string
    building: {
      id: string
    }
  }
}
export interface IPortfolioDepartment {
  allocated: number
  areaSqft: number
  areaSqrm: number
  occupied: number
  seatedCount: number
  unoccupied: number
  department: {
    id: string
    name: string
    color: string
  }
  teams: IPortfolioDepartment[]
}

export type FloorMapSpaces = INormalizedObjects<IFloorMapSpace>
export type FloorMapPins = INormalizedObjects<IFloorMapPin>

export interface ILocation {
  city: string
  state: string
  country: string
  id: string
  title?: string
}

export interface ILocationCapacity {
  capacity: number
  locations: ILocation[]
}

export interface ILocationHealthCheck {
  id: string
  healthScreeningConfirmButtonText?: string
  healthScreeningMessage?: string
  locations: ILocation[]
  followUpQuestionEnabled?: boolean
  followUpQuestionRecipient?: string
  followUpQuestionText: string | undefined
  healthScreeningOverride?: boolean
}

export interface HealthCheck {
  locationHealthGroupId: string
  bookingId: string | undefined
}

export interface ILocationBookableDesk {
  bookableSpaceTypes: string[]
  locations: ILocation[]
}

//admin search types
export interface ISeat {
  id: string
  floorPlanId: string
  external_id: string
  floor: {
    id: string
  }
}

export interface IAdminSearchEmployee {
  id: string
  name: string
  email: string
  noDeskNeeded: boolean
  isRemote: boolean
  startDate: string | null
  endDate: string | null
  assets: {
    url: string | null
  }
  department: {
    id: string
    name: string
    color: string
  }
  team: {
    id: string
    name: string
    color: string
  } | null
  seat: ISeat
  neighborhoods: {
    id: string
    name: string
    building: {
      id: string
      code: string
      name: string
    }
    floor: {
      id: string
      code: string
      name: string
    }
  }[]
  secondarySeats: {
    edges: {
      node: ISeat
    }[]
  }
  hotelingDesks: {
    edges: {
      node: {
        id: string
        external_id: string
      }
    }[]
  }
  bookings?: IEmployeeAdminSearchBookings[]
}

export interface IAdminSearchSpace {
  id: string
  name: string
  spaceType: string
  externalId: string
  spaceInteractivity: string
  floor: {
    id: string
    code: string
    name: string
  }
}

export interface IAdminSearchDepartment {
  id: string
  name: string
  colors: string
}

export interface IAdminSearchNeighborhood {
  id: string
  name: string
  color?: string
  building: {
    id: string
    code: string
    name: string
  }
  floor: {
    id: string
    code: string
    name: string
  }
  type: string
}

export interface IEmployeeWithBookings {
  id: string
  name: string
  bookings: IEmployeeAdminSearchBookings[]
}

export interface IEmployeeAdminSearchBookings {
  startDate: string
  endDate: string
  checkedIn: string
  passedHealthCheck: boolean
  space: {
    id: string
    externalId: string
    x: string
    y: string
    floor: {
      id: string
      code: string
      name: string
      building: {
        id: string
        code: string
        name: string
        city: string
        timezone: string
        address: string
      }
    }
  }
}

export interface IAdminSearchResult {
  employees: IConnection<IAdminSearchEmployee>
  departments: IConnection<IAdminSearchDepartment>
  spaces: IConnection<IAdminSearchSpace>
  neighborhoods: IConnection<IAdminSearchNeighborhood>
}

export interface ISpaceCapacity {
  capacity?: number
  id: string
  measurements?: number
}

type TotalCount = {
  counters: {
    total: string
  }
}
export interface ISearchFilterDetails {
  employeeConnection: TotalCount
  departmentConnection: TotalCount
  spaceConnection: TotalCount
  neighborhoodConnection: TotalCount
}
export type ReturnTypeGetFilterById = {
  id?: string
  key?: string
  name?: string
  parentId?: string
  value?: string
  filters?: number
  objectType?: string
  visible?: number | boolean
  safeDistance?: number
  units: string
  isSafeDistancing?: boolean
  tooltip?: string
  disableOnPrimary?: boolean
}

export type RawViewFilters = {
  name: string
  objectType: string
  filters: Record<string, unknown>[]
}[]

export interface IViewFiltersData {
  viewFilters: any
  getSingleFilterName: (id: string) => string
  getFilterById: (id: string) => ReturnTypeGetFilterById
  toggleChild: (id: string) => void
}

export type ViewFilterChild = {
  children: string[]
  filterId: string
}
